import React, { useCallback, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, EyeOff, HelpCircle, History, MapPin, RefreshCw } from "lucide-react";
import { TrackSignalBadges } from "@/components/ripeness/RadarFilterChips";
import { CONFIDENCE_TONE, FUNNEL_META, TIER_META, fmtDate, isCtisId, registryUrl } from "@/components/ripeness/radarMeta";
import { EvidenceHistory } from "@/components/ripeness/EvidenceHistory";
import { ScoreExplanation } from "@/components/ripeness/ScoreExplanation";
import { TrackSourceBadges } from "@/components/ripeness/SourceBadges";
import { SourceLink, ValueCell } from "@/components/ripeness/ValueCell";
import { SourcesList, ValueCard } from "@/components/ripeness/ValueCard";

function TierBadge({ tier }) {
    const m = TIER_META[tier] || TIER_META.cold;
    const { Icon } = m;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${m.tone}`}
            data-testid={`tier-badge-${tier}`}
        >
            <Icon className="w-3 h-3" /> {m.label}
        </span>
    );
}

function ConfidenceBadge({ value }) {
    const tone = CONFIDENCE_TONE[value] || CONFIDENCE_TONE.none;
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${tone}`}>
            {value || "unknown"}
        </span>
    );
}

function MarketFlags({ markets }) {
    if (!markets) return null;
    const order = ["US", "EU", "CA", "UK"];
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {order.map((k) => {
                const v = markets[k];
                if (!v || v === "none") return null;
                const cls = v === "open"
                    ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10"
                    : "border-amber-400/40 text-amber-300 bg-amber-400/10";
                return (
                    <span key={k} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] uppercase ${cls}`}>
                        <MapPin className="w-2.5 h-2.5" /> {k}
                    </span>
                );
            })}
        </div>
    );
}

const SRC_LABEL = { "ct.gov": "ClinicalTrials.gov", ctis: "CTIS (EU register)", inferred: "Inferred from prior phase", "ai-signal": "AI news / filing signal" };
const ROLE_LABEL = {
    core: "Core trial", supporting: "Supporting study", extension: "Extension study",
    clinpharm: "Clinical pharmacology", next: "Next phase (projected)",
};
const fmtStatus = (s) => (s ? String(s).toLowerCase().replace(/_/g, " ") : "—");
const scoreTone = (s) => (s >= 80 ? "text-rose-300" : s >= 60 ? "text-orange-300" : s >= 40 ? "text-amber-300" : "text-slate-400");
const barTone = (s) => (s >= 80 ? "bg-rose-400" : s >= 60 ? "bg-orange-400" : s >= 40 ? "bg-amber-400" : "bg-slate-500");
const tierOf = (s) => (s >= 80 ? "hot" : s >= 60 ? "warm" : s >= 40 ? "watch" : "cold");
function regionsOf(node) {
    if (!node) return "none listed";
    const r = [node.us && "US", node.eu && "EU", node.ca && "Canada", node.uk && "UK"].filter(Boolean);
    return r.length ? r.join(" · ") : "none listed";
}

function PhaseChip({ phase, node, best }) {
    const s = node?.score ?? null;
    const tone = s === null
        ? "border-slate-800 text-slate-600 bg-slate-900/40"
        : s >= 80 ? "border-rose-400/40 text-rose-200 bg-rose-400/10"
            : s >= 60 ? "border-orange-400/40 text-orange-200 bg-orange-400/10"
                : s >= 40 ? "border-amber-400/40 text-amber-200 bg-amber-400/10"
                    : "border-slate-600/40 text-slate-400 bg-slate-500/5";
    return (
        <div className={`rounded border px-2 py-1 text-center min-w-[48px] ${tone} ${best ? "ring-1 ring-emerald-400/60" : ""}`}
            data-testid={`phase-chip-${phase}`}>
            <div className="text-[11px] font-semibold">P{phase}</div>
            <div className="text-xs">{s === null ? "—" : s}</div>
        </div>
    );
}

function KV({ k, v, mono }) {
    return (
        <div className="flex justify-between gap-3 text-xs py-0.5">
            <span className="text-slate-500">{k}</span>
            <span className={`text-slate-200 text-right ${mono ? "font-mono" : ""}`}>{v ?? "—"}</span>
        </div>
    );
}

function NctLink({ nct, className = "" }) {
    if (!nct) return <span className="text-slate-500">—</span>;
    return (
        <a href={registryUrl(nct)} target="_blank" rel="noreferrer" title={isCtisId(nct) ? "CTIS (EU Clinical Trials Information System)" : "ClinicalTrials.gov"}
            className={`text-sky-300 hover:underline inline-flex items-center gap-1 font-mono ${className}`}
            data-testid={`nct-link-${nct}`}>
            {nct} <ExternalLink className="w-2.5 h-2.5" />
        </a>
    );
}

function PhaseCard({ phase, node, best }) {
    const s = node?.score ?? 0;
    const tier = node ? tierOf(s) : null;
    return (
        <div className={`rounded-lg border ${best ? "border-emerald-400/40" : "border-slate-800"} bg-slate-900/50 p-3`}
            data-testid={`phase-card-${phase}`}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="text-sm font-semibold text-slate-100">Phase {phase}</div>
                {node ? (
                    <div className={`text-xs font-semibold ${scoreTone(s)}`}>{s} · {tier}</div>
                ) : <div className="text-xs text-slate-600">no signal</div>}
            </div>
            {node ? (
                <>
                    <div className="text-xs text-slate-300 leading-snug mb-2 min-h-[2.5rem]">{node.stage || "—"}</div>
                    <KV k="Source" v={SRC_LABEL[node.src] || node.src || "—"} />
                    <KV k="Role" v={ROLE_LABEL[node.role] || node.role || "—"} />
                    <div className="flex justify-between gap-3 text-xs py-0.5">
                        <span className="text-slate-500">Trial</span>
                        <NctLink nct={node.nct} />
                    </div>
                    {node.src === "ct.gov" || node.src === "ctis" ? (
                        <>
                            <KV k="Status" v={fmtStatus(node.status)} />
                            <KV k="Start" v={node.start || "—"} mono />
                            <KV k="Sites listed" v={node.sites === 0 ? "none yet" : (node.sites ?? "—")} />
                            <KV k="Target enrollment" v={node.enroll ?? "—"} />
                            <KV k="Regions" v={regionsOf(node)} />
                        </>
                    ) : null}
                </>
            ) : (
                <div className="text-xs text-slate-600 italic">No registry trial or news signal for this phase.</div>
            )}
        </div>
    );
}

function TrialsList({ trials }) {
    const [open, setOpen] = useState(false);
    if (!trials?.length) return null;
    return (
        <div className="mt-3" data-testid="matched-trials">
            <button onClick={() => setOpen(!open)}
                className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
                data-testid="matched-trials-toggle">
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {trials.length} matched trial{trials.length === 1 ? "" : "s"} (ClinicalTrials.gov · CTIS)
            </button>
            {open ? (
                <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="text-slate-500 text-left">
                            <tr>
                                <th className="py-1 pr-3 font-medium">Trial</th><th className="py-1 pr-3 font-medium">Phase</th>
                                <th className="py-1 pr-3 font-medium">Status</th><th className="py-1 pr-3 font-medium">Start</th>
                                <th className="py-1 pr-3 font-medium">Sites</th><th className="py-1 pr-3 font-medium">Enroll</th>
                                <th className="py-1 pr-3 font-medium">Sponsor</th><th className="py-1 font-medium">Title</th>
                            </tr>
                        </thead>
                        <tbody className="text-slate-300">
                            {trials.map((t) => (
                                <tr key={t.registry_id} className="border-t border-slate-800/80" data-testid={`trial-row-${t.registry_id}`}>
                                    <td className="py-1 pr-3"><NctLink nct={t.registry_id} /></td>
                                    <td className="py-1 pr-3">{(t.phases || []).map((p) => `P${p}`).join("/") || "—"}</td>
                                    <td className="py-1 pr-3">{fmtStatus(t.status)}</td>
                                    <td className="py-1 pr-3 font-mono">{t.start_date || "—"}</td>
                                    <td className="py-1 pr-3">{t.site_count ?? "—"}</td>
                                    <td className="py-1 pr-3">{t.enrollment ?? "—"}</td>
                                    <td className="py-1 pr-3 truncate max-w-[160px]">{t.lead_sponsor || "—"}</td>
                                    <td className="py-1 text-slate-400 truncate max-w-[360px]" title={t.title}>{t.title || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}

function SummaryCell({ label, children, testId, className = "" }) {
    return (
        <div data-testid={testId} className={className}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function DrilldownPanels({ track, onRescored }) {
    const [panel, setPanel] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const rescored = useCallback(() => { setReloadKey((k) => k + 1); onRescored?.(); }, [onRescored]);
    const btn = (k, label, Icon) => (
        <button onClick={() => setPanel(panel === k ? null : k)} data-testid={`dd-${k}-toggle`}
            className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs transition-colors ${panel === k ? "border-sky-400/60 text-sky-200 bg-sky-400/10" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
        </button>
    );
    return (
        <div className="mt-4" data-testid="dd-panels">
            <div className="flex flex-wrap gap-2">{btn("why", "Why this score", HelpCircle)}{btn("history", "Evidence history", History)}</div>
            {panel === "why" ? <ScoreExplanation trackId={track.track_id} programId={track.program_id} onRescored={rescored} /> : null}
            {panel === "history" ? <EvidenceHistory trackId={track.track_id} reloadKey={reloadKey} /> : null}
        </div>
    );
}

function TrackDrilldown({ detail, onExclude, onRestore, onRescored }) {
    const ev = detail.evaluation;
    const track = detail.track;
    const program = detail.program || {};
    const compound = ev?.compound?.name || track.compound?.name || program.compound_name || "—";
    const sponsor = ev?.sponsor?.name || (program.sponsors || [])[0] || "—";
    const ind = ev?.indication?.label || track.indication?.label || track.indication_key || "—";
    const phases = ev?.phases || {};
    const bestP = ev?.best_phase || track.best_phase;
    const bestNode = phases[String(bestP)];
    const score = ev?.ripeness ?? track.ripeness ?? 0;
    const cro = ev?.cro;
    const markets = track.markets || ev?.markets;
    const excluded = detail.excluded;
    const trials = detail.evidence?.registry || [];
    const openTrials = ev?.open_trial_count ?? 0;
    if (!ev) {
        return (
            <div className="border-t border-slate-800 bg-slate-950/40 px-4 py-4 text-xs text-slate-400" data-testid="track-drilldown-empty">
                No evaluation snapshot stored for this track yet.
            </div>
        );
    }
    return (
        <div className="border-t border-slate-800 bg-slate-950/40 px-4 py-4" data-testid="track-drilldown">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
                <SummaryCell label="Compound" testId="dd-compound">
                    <div className="text-sm font-semibold text-slate-100">{compound}</div>
                    <div className="text-xs text-sky-300">{sponsor}</div>
                    {program.sponsors?.length > 1 ? (
                        <div className="text-[11px] text-slate-500 truncate" title={program.sponsors.join(" · ")}>
                            +{program.sponsors.length - 1} related sponsor name{program.sponsors.length > 2 ? "s" : ""}
                        </div>
                    ) : null}
                    {cro?.name ? (
                        <div className="mt-1 text-xs text-slate-300 flex items-center gap-1.5 flex-wrap">
                            CRO <span className="text-sky-300">{cro.name}</span>
                            <span className={`rounded border px-1.5 py-0.5 text-[10px] font-mono ${cro.conf === "confirmed" ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>
                                {cro.conf}
                            </span>
                        </div>
                    ) : null}
                </SummaryCell>
                <SummaryCell label="Indication track" testId="dd-indication">
                    <div className="text-sm font-semibold text-sky-300">{ind}</div>
                    <div className="text-xs text-slate-400">Area: <span className="text-sky-300">{ev.therapeutic_area || track.therapeutic_area || "—"}</span></div>
                    <div className="text-xs text-slate-400">{ev.lifecycle?.label || `Phase ${bestP || "—"}`}</div>
                    {detail.sibling_tracks?.length ? (
                        <div className="text-[11px] text-slate-500 mt-1">
                            All {detail.sibling_tracks.length + 1} tracks for this compound →
                            <span className="text-slate-400"> {detail.sibling_tracks.slice(0, 3).map((s) => `${s.indication} (${s.ripeness ?? 0})`).join(" · ")}{detail.sibling_tracks.length > 3 ? " · …" : ""}</span>
                        </div>
                    ) : null}
                </SummaryCell>
                <SummaryCell label="Ripeness (best phase)" testId="dd-ripeness">
                    <div className="flex items-center gap-2">
                        <span className={`text-xl font-semibold ${scoreTone(score)}`}>{score}</span>
                        <div className="flex-1 h-1.5 rounded bg-slate-800 overflow-hidden">
                            <div className={`h-full ${barTone(score)}`} style={{ width: `${Math.min(100, score)}%` }} />
                        </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">Best P{bestP || "—"} · {tierOf(score)}</div>
                </SummaryCell>
                <SummaryCell label="Funnel position" testId="dd-funnel">
                    <div className="text-xs text-slate-200 leading-snug">{bestNode?.stage || FUNNEL_META[ev.funnel_stage]?.label || ev.funnel_stage || "—"}</div>
                    <div className="mt-1"><NctLink nct={bestNode?.nct} /></div>
                </SummaryCell>
                <SummaryCell label="P1 · P2 · P3 · P4" testId="dd-phases" className="xl:col-span-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {[1, 2, 3, 4].map((p) => (
                            <PhaseChip key={p} phase={p} node={phases[String(p)]} best={String(p) === String(bestP)} />
                        ))}
                    </div>
                </SummaryCell>
                <SummaryCell label="Markets" testId="dd-markets">
                    <MarketFlags markets={markets} />
                    {!markets || Object.values(markets).every((v) => v === "none") ? (
                        <div className="text-[11px] text-slate-500">no open sites listed</div>
                    ) : null}
                    {ev.sites_pending ? <div className="text-[10px] text-amber-300 uppercase tracking-wider mt-1">sites pending</div> : null}
                </SummaryCell>
                <SummaryCell label="Match · Last AI signal" testId="dd-match">
                    <ConfidenceBadge value={ev.match_confidence} />
                    <div className="text-xs text-slate-300 mt-1">{ev.trial_count ?? 0} trials · {openTrials} open</div>
                    <div className="text-xs text-slate-400 font-mono mt-1">{fmtDate(ev.last_signal_at || ev.latest_note?.published_at)}</div>
                    <div className="text-[11px] text-slate-500">{ev.mentions ?? 0} mentions</div>
                </SummaryCell>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-4">
                {[1, 2, 3, 4].map((p) => (
                    <PhaseCard key={p} phase={p} node={phases[String(p)]} best={String(p) === String(bestP)} />
                ))}
            </div>

            <DrilldownPanels track={track} onRescored={onRescored} />

            <div className="mt-4 text-xs text-slate-300 leading-relaxed" data-testid="dd-latest-note">
                <b className="text-slate-100">Latest AI note</b>
                <span className="text-slate-500"> ({fmtDate(ev.latest_note?.published_at)})</span>: {(ev.latest_note?.summary || "—").slice(0, 420)}
                {ev.latest_note?.url ? (
                    <a href={ev.latest_note.url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-1 ml-1">
                        Source article <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                ) : null}
            </div>
            <ValueCard value={detail.value} byPhase={detail.value_by_phase} />
            <SourcesList sources={track.sources} />
            {cro?.name ? (
                <div className="mt-2 text-xs text-slate-400" data-testid="dd-cro">
                    <b className="text-slate-100">CRO:</b> {cro.name} is listed as collaborator on this track&apos;s trial{cro.evidence?.length > 1 ? "s" : ""}{" "}
                    {(cro.evidence || []).slice(0, 3).map((n, i) => <span key={n}>{i ? ", " : ""}<NctLink nct={n} /></span>)}.
                    <span className="text-slate-500"> A suggestion only: confirm before recording it on a lead.</span>
                </div>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500" data-testid="dd-footer">
                <div>
                    Track: {compound} × {ind} · {ev.trial_count ?? 0} matched trials in this indication ({openTrials} open) ·{" "}
                    {ev.mentions ?? 0} AI records for this indication · {ev.program_mentions ?? 0} AI records for the compound overall
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {ev.lifecycle?.next_gate ? <span>Next gate: <span className="text-slate-300">{ev.lifecycle.next_gate}</span>{ev.timing?.readout ? ` · readout ${ev.timing.readout}` : ""}</span> : null}
                    {ev.timing?.loa ? <span>LoA {(ev.timing.loa * 100).toFixed(0)}%</span> : null}
                    <span className="font-mono">{ev.evaluation_id}</span>
                    {excluded ? (
                        <span className="inline-flex items-center gap-2" data-testid="dd-excluded-badge">
                            <span className="rounded border border-rose-400/40 text-rose-300 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" title={excluded.reason || ""}>
                                excluded{excluded.scope === "program" ? " (program)" : ""}
                            </span>
                            <button onClick={() => onRestore(excluded)} className="text-emerald-300 hover:underline" data-testid="dd-restore-btn">
                                Restore
                            </button>
                        </span>
                    ) : (
                        <button onClick={() => onExclude(track)} className="text-rose-300 hover:underline inline-flex items-center gap-1" data-testid="dd-exclude-btn">
                            <EyeOff className="w-3 h-3" /> Exclude this track
                        </button>
                    )}
                </div>
            </div>
            <TrialsList trials={trials} />
        </div>
    );
}

export function TrackRow({ track, expanded, onToggle, detail, loadingDetail, onExclude, onRestore, onRescored }) {
    const ind = track.indication?.label || track.indication_key || "Unspecified";
    const compound = track.compound?.name || "—";
    return (
        <div
            className="rounded-lg border border-slate-700/60 bg-slate-900/40 mb-2"
            data-testid={`track-row-${track.track_id}`}
        >
            <div className="flex items-center pr-3">
                <button
                    onClick={onToggle}
                    className="flex-1 min-w-0 text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-800/40"
                    data-testid={`track-toggle-${track.track_id}`}
                >
                    {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <div className="w-16 text-right">
                        <div className="text-2xl font-semibold text-slate-100">{track.ripeness ?? 0}</div>
                        <div className="text-[10px] text-slate-500">RIPENESS</div>
                    </div>
                    <TierBadge tier={track.tier} />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-200 inline-flex items-center gap-2">
                            {compound}
                            {track.excluded ? (
                                <span className="rounded border border-rose-400/40 text-rose-300 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" data-testid={`track-excluded-${track.track_id}`}>
                                    excluded
                                </span>
                            ) : null}
                        </div>
                        <div className="text-xs text-slate-400">{ind}</div>
                        <div className="flex flex-wrap items-center gap-1">
                            <TrackSignalBadges track={track} />
                            <TrackSourceBadges track={track} />
                        </div>
                    </div>
                    <div className="hidden md:block text-xs text-slate-400">
                        {track.therapeutic_area || "—"}
                    </div>
                    <div className={`hidden md:block text-xs ${(FUNNEL_META[track.funnel_stage]?.tone) || "text-slate-400"}`}>
                        {FUNNEL_META[track.funnel_stage]?.label || track.funnel_stage || "—"}
                    </div>
                    <div className="hidden lg:block text-xs text-slate-400">Best P{track.best_phase || "—"}</div>
                    <div className="hidden lg:block text-xs text-slate-400">Next: {fmtDate(track.next_reevaluation_due)}</div>
                    <ValueCell track={track} />
                </button>
                <SourceLink track={track} className="ml-1 shrink-0" />
            </div>
            {expanded && loadingDetail ? (
                <div className="border-t border-slate-800 bg-slate-950/40 px-4 py-4 text-xs text-slate-400 inline-flex items-center gap-2" data-testid="track-drilldown-loading">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading evaluation…
                </div>
            ) : null}
            {expanded && !loadingDetail && detail ? <TrackDrilldown detail={detail} onExclude={onExclude} onRestore={onRestore} onRescored={onRescored} /> : null}
        </div>
    );
}

